package ui.campaign.detailv2.editor.page

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.*
import androidx.compose.material3.Button
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import api.ResourceType
import api.VisualRotaryMode
import org.ssoft.common.type.ScreenRatio
import ssoft.frontend.generated.resources.*
import tools.rememberString
import ui.campaign.detailv2.editor.CampaignEditorScope
import ui.campaign.detailv2.editor.CampaignEditorVM
import ui.campaign.detailv2.editor.wrapper.CampaignWrapperE
import ui.campaign.detailv2.widgets.*
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds

@Composable
fun CampaignEditorScope.CampaignPage08Summary(nnvl: CampaignWrapperE) {
    val primaryVisuals = remember {
        nnvl.visual.adapterVisuals.state
            .filter { it.data.mask in nnvl.visual.deviceRatioMasks }
            .filter { it.resource != null }
            .map { visual ->
                ImplCampaignInfoCard.Visual(
                    id = visual.resource!!.resourceId,
                    width = visual.resource!!.width,
                    height = visual.resource!!.height,
                    duration = visual.resource!!.duration.milliseconds,
                    type = ResourceType[visual.resource!!.type],
                    ratio = ScreenRatio[visual.data.mask]
                )
            }
    }
    LazyVerticalStaggeredGrid(
        columns = StaggeredGridCells.FixedSize(512.dp),
        contentPadding = PaddingValues(vertical = 16.dp),
        horizontalArrangement = Arrangement.SpaceEvenly,
        verticalItemSpacing = 16.dp,
        modifier = Modifier.fillMaxSize()
    ) {
        CardMainInfo(nnvl)
        CardDevices(nnvl)
        CardVisuals(nnvl, primaryVisuals)
        CardOffsets(nnvl)
        CardVisualDynamic(nnvl)
        CardApps(nnvl, primaryVisuals)
        CardMiniApp(nnvl)

        if (nnvl.isNewCampaign) {
            item(span = StaggeredGridItemSpan.FullLine) {
                Box {
                    Button(
                        onClick = { action(CampaignEditorVM.Action.Activate) },
                        modifier = Modifier.width(512.dp)
                            .align(Alignment.Center)
                    ) {
                        Text(
                            text = Res.string.layer_top_detail_editor__action_summary_new_activate_save.rememberString(),
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }

            item(span = StaggeredGridItemSpan.FullLine) {
                Box {
                    Button(
                        onClick = { action(CampaignEditorVM.Action.Save) },
                        modifier = Modifier.width(512.dp)
                            .align(Alignment.Center)
                    ) {

                        Text(
                            text = Res.string.layer_top_detail_editor__action_summary_new_save.rememberString(),
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }
        } else {
            if (nnvl.isStateOfDraft) {
                item(span = StaggeredGridItemSpan.FullLine) {
                    Box {
                        Button(
                            onClick = { action(CampaignEditorVM.Action.ChangeDraft) },
                            modifier = Modifier.width(512.dp)
                                .align(Alignment.Center)
                        ) {
                            Text(
                                text = Res.string.layer_top_detail_editor__action_summary_new_save.rememberString(),
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                }

                item(span = StaggeredGridItemSpan.FullLine) {
                    Box {
                        Button(
                            onClick = { action(CampaignEditorVM.Action.ActivateDraft) },
                            modifier = Modifier.width(512.dp)
                                .align(Alignment.Center)
                        ) {
                            Text(
                                text = Res.string.layer_top_detail_editor__action_summary_new_activate_save.rememberString(),
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                }
            } else {
                item(span = StaggeredGridItemSpan.FullLine) {
                    Box {
                        Button(
                            onClick = { action(CampaignEditorVM.Action.Change) },
                            modifier = Modifier.width(512.dp)
                                .align(Alignment.Center)
                        ) {
                            Text(
                                text = Res.string.layer_top_detail_editor__action_summary_old_apply.rememberString(),
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                }
            }
            item(span = StaggeredGridItemSpan.FullLine) {
                Box {
                    Button(
                        onClick = { nav.up() },
                        modifier = Modifier.width(512.dp)
                            .align(Alignment.Center)
                    ) {
                        Text(
                            text = Res.string.layer_top_detail_editor__action_summary_old_discard.rememberString(),
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }
        }
    }
}


private fun LazyStaggeredGridScope.CardMainInfo(nnvl: CampaignWrapperE) {
    item {
        CampaignInfoCardMain(
            type = nnvl.type,
            name = nnvl.general.name.state,
            dtStart = nnvl.general.dtStart.state.date,
            dtEnd = nnvl.general.dtEnd.state.date,
            volume = nnvl.general.volume.state,
            enableCompanyCommercial = nnvl.data.settings.enableCommercial,
            isCom = nnvl.general.isCommercial.state,
            isNCom = nnvl.general.isNonCommercial.state,
            countLocation = nnvl.location.locations.state.count { it.devices.state.any { it.active.state } },
            countDevice = nnvl.location.locations.state.sumOf { it.devices.state.count { it.active.state } },
            countVisuals = nnvl.visual.deviceRatioMasks.count(),
            impressionsSum = nnvl.location.locations.state.sumOf {
                it.devices.state.filter { it.active.state }.sumOf { it.impressionSum }
            },
            impDur = nnvl.general.impressionDuration.state.seconds,
            impCount = nnvl.general.impressionCount.state,
            impSpace = nnvl.general.impressionSpace.state,
            slotDur = (nnvl.general.slotsCount.state * nnvl.data.settings.companyDuration).milliseconds,
            slotCount = nnvl.general.slotsCount.state,
            slotCountCompany = nnvl.data.settings.companyCount,

            countApps = nnvl.apps.adapterApps.state.size,
            appBehavior = nnvl.data.appBehavior,
            visualRotaryMode = if (nnvl.data.visuals.size > 1) nnvl.data.visualRotaryMode else VisualRotaryMode.NONE.mask,

            )
    }
}

private fun LazyStaggeredGridScope.CardOffsets(nnvl: CampaignWrapperE) {
    nnvl.data.otherCampaignGroups.filter { it.exclude }
        .takeIf { it.isNotEmpty() }
        ?.also { items ->
            item {
                CampaignCardOffsets(items)
            }
        }
}

private fun LazyStaggeredGridScope.CardDevices(nnvl: CampaignWrapperE) {
    nnvl.location.locations.state
        .filter { it.devices.state.any { it.active.state } }
        .map { loc ->
            ImplCampaignInfoCard.Location(
                id = loc.data.id,
                name = loc.data.name,
                devices = loc.devices.state
                    .filter { it.active.state }
                    .map { device ->
                        ImplCampaignInfoCard.Device(
                            id = device.data.id,
                            name = device.data.name,
                            countUsableDays = device.countUsableDays,
                            impressionSum = device.impressionSum
                        )
                    }
            )
        }.also {
            item {
                CampaignInfoCardDevices(
                    showCounters = nnvl.general.isCommercial.state,
                    locations = it
                )
            }
        }
}

private fun LazyStaggeredGridScope.CardVisuals(
    nnvl: CampaignWrapperE,
    primaryVisuals: List<ImplCampaignInfoCard.Visual>
) {
    item {
        CampaignInfoCardVisuals(primaryVisuals)
    }
}

private fun LazyStaggeredGridScope.CardVisualDynamic(nnvl: CampaignWrapperE) {
    nnvl.visualDynamic.adapterGroups.state
        .filter { it.visuals.state.any { it.resource != null } }
        .map { group ->
            ImplCampaignInfoCard.VisualDynamic(
                conditions = group.data.conditions,
                weatherPictogramVariants = nnvl.data.weatherPictograms,
                visuals = group.visuals.state
                    .mapNotNull { it.resource }
                    .map {
                        ImplCampaignInfoCard.Visual(
                            id = it.resourceId,
                            width = it.width,
                            height = it.height,
                            duration = it.duration.milliseconds,
                            type = ResourceType[it.type],
                            ratio = ScreenRatio[it.screenRatio]
                        )
                    }
            )

        }.also { items ->
            items(items) {
                CampaignInfoCardVisualsDynamic(it)
            }
        }
}

private fun LazyStaggeredGridScope.CardApps(nnvl: CampaignWrapperE, primaryVisuals: List<ImplCampaignInfoCard.Visual>) {
    nnvl.apps.adapterApps.state
        .map { item -> item.data }
        .also { apps ->
            items(apps) {
                CampaignInfoCardApplication(
                    config = it,
                    visuals = primaryVisuals,
                    pictograms = nnvl.data.weatherPictograms
                )
            }
        }
}

private fun LazyStaggeredGridScope.CardMiniApp(nnvl: CampaignWrapperE) {
    val miniApp = nnvl.data.miniApp.firstOrNull { it.enabled } ?: return
    item {
        CampaignInfoCardMiniApp(miniApp)
    }
}
